import React, { useEffect, useRef, useState } from "react";
import { NavLink, useNavigate, useLocation, Outlet } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { WS_URL, api } from "@/lib/api";
import { ComplianceBar, ComplianceFooter } from "@/components/ComplianceBanner";
import { Ticker } from "@/components/Ticker";
import { Button } from "@/components/ui/button";
import {
  Radar, Bookmark, Grid3x3, SlidersHorizontal,
  LogOut, Menu, X, LayoutDashboard, Zap, Layers, Crown, Flame, BarChart3, Gem,
} from "lucide-react";
import { BrandLogo } from "@/components/Brand";

const NAV = [
  { to: "/app/today", label: "Today", icon: LayoutDashboard, testid: "nav-today", group: "main" },
  { to: "/app/scanner", label: "Institutional", icon: Radar, testid: "nav-scanner", group: "scan" },
  { to: "/app/opening-adr", label: "Opening Thrust", icon: Zap, testid: "nav-opening-adr", group: "scan" },
  { to: "/app/value-area", label: "Value Nodes", icon: Layers, testid: "nav-value-area", group: "scan" },
  { to: "/app/tfr-gold", label: "TFR Gold", icon: Crown, testid: "nav-tfr-gold", group: "scan" },
  { to: "/app/power-scan", label: "Power Scan", icon: Flame, testid: "nav-power-scan", group: "scan" },
  { to: "/app/platinum", label: "Platinum", icon: Gem, testid: "nav-platinum", group: "scan" },
  { to: "/app/indices", label: "Indices", icon: BarChart3, testid: "nav-indices", group: "more" },
  { to: "/app/heatmap", label: "Heatmap", icon: Grid3x3, testid: "nav-heatmap", group: "more" },
  { to: "/app/watchlists", label: "Watchlists", icon: Bookmark, testid: "nav-watchlists", group: "more" },
];

export function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [feed, setFeed] = useState(null);
  const wsRef = useRef(null);

  // Dhan feed health dot — polls every 60s
  useEffect(() => {
    const load = () => api.get("/today/feed-status").then((r) => setFeed(r.data)).catch(() => {});
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, []);

  const feedDot = (() => {
    if (!feed) return null;
    let cls, label, note = null;
    const last = feed.last_refresh_at ? new Date(feed.last_refresh_at).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: false, timeZone: "Asia/Kolkata" }) : "—";
    if (feed.live_enabled === false) {
      cls = "bg-slate-300"; label = "Preview — live Dhan data runs on the live app only";
    } else if (feed.reauth_required) {
      cls = "bg-rose-500"; label = "Dhan session expired — Reconnect in Admin → Data Feed"; note = "Dhan login expired";
    } else if (!feed.connected) {
      cls = "bg-rose-500"; label = "Dhan disconnected — re-auth in Admin → Dhan"; note = "Dhan disconnected";
    } else if (feed.rest_error) {
      cls = "bg-rose-500"; label = `Dhan is rejecting data requests — ${feed.rest_error.slice(0, 140)}`; note = "Dhan data request failing";
    } else if (!feed.market_open) {
      cls = "bg-slate-400"; label = "Dhan idle — market closed";
    } else {
      const age = feed.last_refresh_at ? (Date.now() - new Date(feed.last_refresh_at).getTime()) / 1000 : 9999;
      const got = Object.values(feed.last_refresh_counts || {}).some((n) => n > 0);
      if (feed.feed_running && age < 180 && got) {
        cls = "bg-emerald-500 live-dot"; label = `Dhan live — refreshed ${Math.round(age)}s ago`;
      } else {
        cls = "bg-amber-500"; label = `Dhan data delayed (last refresh ${Math.round(age / 60)}m ago)`;
        note = !feed.feed_running ? "Live feed not running" : !got ? "Dhan sent no new candles" : "Data refresh delayed";
      }
    }
    return (
      <span className="flex flex-wrap items-center gap-1.5" title={label} data-testid="dhan-status-dot" data-state={cls.includes("emerald") ? "live" : cls.includes("rose") ? "down" : cls.includes("amber") ? "stale" : "idle"}>
        <span className={`h-2 w-2 rounded-full ${cls}`} />
        <span className="font-mono text-[10px] text-slate-400 uppercase">Dhan</span>
        {note && <span className={`font-mono text-[10px] ${cls.includes("rose") ? "text-rose-400" : "text-amber-400"}`} data-testid="dhan-status-note">{note} · last update {last}</span>}
      </span>
    );
  })();

  useEffect(() => {
    let ws;
    try {
      ws = new WebSocket(WS_URL);
      wsRef.current = ws;
      ws.onmessage = (evt) => {
        try {
          const msg = JSON.parse(evt.data);
          if (msg.type === "confluence" && msg.setup) {
            const s = msg.setup;
            toast(`⚡ TFR PRIME · ${s.direction} · ${s.display_symbol}`, {
              description: `Trend Stack + Flow Zones aligned ${s.direction === "BULLISH" ? "up" : "down"} · ₹${s.price}`,
              action: { label: "View", onClick: () => navigate("/app/today") },
            });
            window.dispatchEvent(new Event("confluence-update"));
          }
        } catch { /* noop */ }
      };
    } catch { /* noop */ }
    return () => ws && ws.close();
  }, [navigate]);

  const items = user?.role === "admin"
    ? [...NAV, { to: "/app/admin", label: "Admin", icon: SlidersHorizontal, testid: "nav-admin", group: "more" }]
    : NAV;

  const groups = [
    [null, items.filter((n) => n.group === "main")],
    ["Scanners", items.filter((n) => n.group === "scan")],
    ["More", items.filter((n) => n.group === "more")],
  ];
  const sideNav = (onPick) => groups.map(([g, list]) => (
    <div key={g || "main"} className="mb-1">
      {g && <div className="px-3 pt-4 pb-1 text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-400">{g}</div>}
      {list.map((n) => (
        <NavLink key={n.to} to={n.to} data-testid={n.testid} onClick={onPick}
          className={({ isActive }) => `flex items-center gap-2.5 px-3 py-[7px] rounded-md text-[13px] transition-colors ${isActive ? "bg-slate-100 text-slate-900 font-semibold" : "text-slate-500 hover:text-slate-900 hover:bg-slate-50"}`}>
          <n.icon className="h-4 w-4 shrink-0" strokeWidth={1.75} /> {n.label}
        </NavLink>
      ))}
    </div>
  ));

  return (
    <div className="min-h-screen flex bg-[#fafaf9]">
      <aside className="hidden md:flex w-56 shrink-0 flex-col border-r border-slate-200 bg-white sticky top-0 h-screen" data-testid="sidebar">
        <div className="h-14 flex items-center gap-2.5 px-4 cursor-pointer" onClick={() => navigate("/app/today")}>
          <BrandLogo markClass="h-8 w-8" showText={false} live />
          <span className="font-display text-sm font-semibold tracking-tight text-slate-900">TFR Scanner</span>
        </div>
        <nav className="flex-1 overflow-y-auto px-2 pb-4">{sideNav()}</nav>
        <div className="border-t border-slate-200 p-3 space-y-2">
          {feedDot}
          <div className="flex items-center justify-between text-xs">
            <span className="text-slate-600 truncate">{user?.name}</span>
            <span className="flex items-center gap-1">
              <span className={`px-1.5 py-0.5 rounded font-mono text-[10px] uppercase ${user?.subscription?.active ? "text-emerald-700 bg-emerald-50" : "text-rose-700 bg-rose-50"}`}>{user?.subscription?.plan || "—"}</span>
              <Button variant="ghost" size="icon" onClick={logout} data-testid="logout-button" className="h-7 w-7 text-slate-400 hover:text-slate-900 hover:bg-slate-100">
                <LogOut className="h-3.5 w-3.5" />
              </Button>
            </span>
          </div>
        </div>
      </aside>
      <div className="flex-1 min-w-0 flex flex-col">
        <ComplianceBar />
        <header className="sticky top-0 z-40 bg-white/90 backdrop-blur border-b border-slate-200">
          <div className="md:hidden flex items-center h-12 px-4 gap-3">
            <BrandLogo markClass="h-7 w-7" showText={false} live />
            <span className="font-display text-sm font-semibold text-slate-900">TFR Scanner</span>
            <div className="flex-1" />
            <Button variant="ghost" size="icon" className="text-slate-600" onClick={() => setMobileOpen(!mobileOpen)} data-testid="mobile-menu-button">
              {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </Button>
          </div>
          <AnimatePresence>
            {mobileOpen && (
              <motion.nav initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }}
                className="md:hidden overflow-hidden border-t border-slate-200 px-2 py-2 bg-white">
                {sideNav(() => setMobileOpen(false))}
                <div className="border-t border-slate-200 mt-1 pt-2 px-3 flex items-center justify-between text-xs">
                  {feedDot}
                  <span className="flex items-center gap-2">
                    <span className="text-slate-600">{user?.name}</span>
                    <span className={`px-1.5 py-0.5 rounded font-mono text-[10px] uppercase ${user?.subscription?.active ? "text-emerald-700 bg-emerald-50" : "text-rose-700 bg-rose-50"}`}>{user?.subscription?.plan || "—"}</span>
                    <Button variant="ghost" size="icon" onClick={logout} data-testid="mobile-logout-button" className="h-7 w-7 text-slate-500 hover:text-slate-900">
                      <LogOut className="h-3.5 w-3.5" />
                    </Button>
                  </span>
                </div>
              </motion.nav>
            )}
          </AnimatePresence>
          <Ticker />
        </header>
        <main className="flex-1 w-full max-w-[1500px] mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <AnimatePresence mode="wait">
            <motion.div key={location.pathname}
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.18, ease: "easeOut" }}>
              {user?.role === "admin" || user?.subscription?.active ? <Outlet /> : (
                <div className="max-w-lg mx-auto mt-16 text-center rounded-lg border border-slate-200 bg-white p-8" data-testid="pending-activation">
                  <h2 className="font-display text-xl font-bold text-slate-900">Account pending activation</h2>
                  <p className="text-sm text-slate-500 mt-2">Your TFR subscription isn't active yet. Contact the TFR desk to activate your access.</p>
                </div>
              )}
            </motion.div>
          </AnimatePresence>
        </main>
        <ComplianceFooter />
      </div>
    </div>
  );
}
